<template>
    <div class="edit-notification-cc">
        <h2 class="tab-title">Select A User to View Their CC Settings</h2>
        <div class="actions-container">
            <k-dropdown class="select-user-dropdown"
                        :data-items="filteredAzureUsers"
                        :text-field="'name'"
                        :data-item-key="'id'"
                        v-model="selectedAzureUser"
                        :filterable="true"
                        @filterchange="onUserFilterChange"
                        @change="onUserSelected"></k-dropdown>
        </div>
        <div class="pipeline-container">
            <command-center-table
                title="CC Settings"
                icon="fa-light fa-envelopes"
                :columns="pipelineColumns"
                :data="filteredUsersCCSettings"
                :selectable="true"
                :selected-field="selectedField"
                :all-selected="areAllSettingsSelected"
                :filterable="false"
                :default-page-size="50"
                :active-sort="sort"
                @row-click="onRowClick"
                @toggle-selected="onSettingSelectionChange"
                @toggle-all="onHeaderSelectionChange"
                @sort="onSortChange"
                @filter-change="onColFilterChange"
            >
                <template #toolbar>
                    <span class="k-textbox k-grid-search k-display-flex">
                        <k-input :style="{ width: '300px' }"
                                placeholder="Search in all columns..."
                                :value="searchWord"
                                :icon-name="'search'"
                                @input="onSearchChange"></k-input>
                    </span>
                    <k-button class="delete-selected-btn" @click="onDeleteSelectedSettings" :disabled="deleteSelectedBtnDisabled">Delete Selected</k-button>
                </template>

                <!-- Actions cell slot -->
                <template #pipelineActions="{ dataItem }">
                    <div class="pipeline-actions-container">
                        <k-button class="delete-item__btn" @click.stop="onDeleteSetting(dataItem)"><i class="fa-duotone fa-trash" title="Delete Setting"></i></k-button>
                        <k-button class="delete-item__btn" @click.stop="onEditSetting(dataItem)"><i class="fa-duotone fa-pen-to-square" title="Edit Setting"></i></k-button>
                    </div>
                </template>
            </command-center-table>
        </div>

        <confirm-dialog :showDialog="showConfirmDialog"
                        :dialog-Msg="confirmMsg"
                        @dialogclosed="toggleConfirmDialog"
                        @cancelclicked="toggleConfirmDialog"
                        @confirmclicked="deleteSettings"></confirm-dialog>

        <edit-setting-dialog :showDialog="showEditSettingDialog"
                             :setting="selectedCCSetting"
                             :notifications="notifications"
                             @dialogclosed="toggleEditSettingDialog"
                             @settingupdated="onSettingUpdated"></edit-setting-dialog>

        <div class="component__loader-container" v-if="loading">
            <k-loader size="large" type="infinite-spinner"></k-loader>
        </div>

        <k-notification-group class="default__notification-container">
            <k-fade :appear="successNotification">
                <k-notification v-if="successNotification"
                                :type="{ style: 'success', icon: true }"
                                :closable="true"
                                @close="onCloseNotification('successNotification')">
                    <span>{{ notificationMsg }}</span>
                </k-notification>
            </k-fade>
        </k-notification-group>

        <transition name="dialogfade">
            <k-dialog v-if="showErrorDialog" :title="errorTitle" class="error__dialog" @close="toggleErrorDialog">
                <p class="error-dialog__msg">{{ errorMsg }}</p>
            </k-dialog>
        </transition>
    </div>
</template>

<script>
    import { Loader } from "@progress/kendo-vue-indicators";
    import { DropDownList } from '@progress/kendo-vue-dropdowns';
    import { Input } from '@progress/kendo-vue-inputs';
    import { Button } from "@progress/kendo-vue-buttons";
    import { Notification, NotificationGroup } from "@progress/kendo-vue-notification";
    import { Fade } from "@progress/kendo-vue-animation";
    import { Dialog } from '@progress/kendo-vue-dialogs';

    import { orderBy, filterBy } from '@progress/kendo-data-query';

    import CommandCenterTable from './commandCenter/CommandCenterTable.vue';
    import confirmDialog from '../components/confirmDialog.vue';
    import editSettingDialog from '../components/editSettingDialog.vue';

    import apiService from '../api/api.service.js';
    import auth from '../authConfig.js';

    import cloneDeep from 'lodash.clonedeep';

    export default {
        name: 'EditNotificationCC',
        props: ['azureUsers', 'notifications'],
        data() {
            return {
                azureId: null,
                confirmMsg: '',
                deleteItems: [],
                errorMsg: '',
                errorTitle: '',
                filter: null,
                filteredAzureUsers: [],
                filteredUsersCCSettings: [],
                loading: true,
                notificationMsg: '',
                searchWord: '',
                selectedAzureUser: { name: 'Select User', email: null, id: Math.random().toString() },
                selectedCCType: 'To',
                selectedField: 'selected',
                selectedCCSetting: null,
                showConfirmDialog: false,
                showEditSettingDialog: false,
                showErrorDialog: false,
                sort: [],
                successNotification: false,
                take: 50,
                unfilteredAzureUsers: [],
                usersCCSettings: []
            }
        },
        computed: {
            areAllSettingsSelected() {
                return this.selectedCCRows.length === this.filteredUsersCCSettings.length && this.selectedCCRows.length > 0;
            },
            deleteSelectedBtnDisabled() {
                return !this.usersCCSettings.some(setting => setting.selected);
            },
            pipelineColumns() {
                return [
                    { field: 'email_address', title: 'Notifications To' },
                    { field: 'templateName', title: 'Notification Title' },
                    { field: 'templateSubject', title: 'Notification Subject' },
                    { field: 'displayName', title: 'CC User Name' },
                    { field: 'cc_address', title: 'CC User Email' },
                    { field: 'actions', title: '', cell: 'pipelineActions', filterable: false, sortable: false, width: '100' }
                ]
            },
            selectedCCRows() {
                return this.usersCCSettings.filter(setting => setting.selected === true);
            }
        },
        async mounted() {
            const msalAppInstance = await auth.init();

            msalAppInstance.handleRedirectPromise().then(async (tokenRes) => {
                if (!tokenRes) {
                    if (!auth.user()) {
                        auth.login();
                    }
                }

                this.azureId = localStorage.getItem('azure_id');

                if (!this.azureId) return;

                if (this.azureUsers.length > 0) {
                    this.unfilteredAzureUsers = cloneDeep(this.azureUsers);
                    this.filteredAzureUsers = cloneDeep(this.azureUsers);
                } else {
                    await this.getUsersByOrgId();
                }

                if (this.filteredAzureUsers.length === 1) {
                    this.selectedAzureUser = this.filteredAzureUsers[0];
                    this.getUsersNotificationCCSettings(this.selectedAzureUser.email);
                }
                this.loading = false;
            });
        },
        methods: {
            assignCCSettings(settingsData) {
                settingsData.forEach(setting => {
                    setting.selected = false;

                    if (!setting.template_id) {
                        setting.templateName = 'All Notifications';
                    } else {
                        let notification = this.notifications.find(noti => noti.id === setting.template_id);

                        if (notification) {
                            setting.templateName = notification.displayName;
                            setting.templateSubject = notification.subject;
                        }
                    }
                });

                this.usersCCSettings = settingsData;
                this.getData();
            },
            getData() {
                this.filteredUsersCCSettings = this.getFilteredItems();
            },
            getFilteredItems() {
                let items;

                if (this.searchWord) {
                    items = this.usersCCSettings.filter(item => {
                        return Object.values(item).some(val => val && val.toString().toLowerCase().includes(this.searchWord.toLowerCase()));
                    });
                } else {
                    items = cloneDeep(this.usersCCSettings);
                }

                if (this.sort.length > 0) {
                    items = orderBy(items, this.sort);
                }

                if (this.filter) {
                    items = filterBy(items, this.filter);
                }

                return items;
            },
            async deleteSettings() {
                this.toggleConfirmDialog();

                this.loading = true;

                let request = {
                    userEmail: this.selectedAzureUser.email,
                    type: 'To',
                    settingIds: this.deleteItems.map(item => item.guid)
                }

                const tokenResponse = await auth.acquireTokenResponse();

                return apiService.DeleteNotificationCCSettings(request, tokenResponse.accessToken)
                    .then(res => {
                        this.loading = false;

                        if (res?.data?.data) {
                            this.assignCCSettings(res.data.data);
                            this.showNotification('Setting deleted successfully');
                        }
                    }).catch(error => {
                        let errorTitle = 'Failed to Delete Setting';

                        let errorMsg;

                        if(error.response?.data?.data) {
                            errorMsg = "Error: \n\n" + error.response.data.data;
                        } else {
                            errorMsg = '';
                        }

                        this.onRequestError(errorTitle, errorMsg);
                    });
            },
            async getUsersByOrgId() {
                const tokenResponse = await auth.acquireTokenResponse();

                return apiService.GetUsersByOrgIdByRole(this.azureId, tokenResponse.accessToken)
                    .then(res => {
                        if (res.data?.data) {
                            this.unfilteredAzureUsers = res.data.data.map(user => {
                                return {
                                    name: user.displayName,
                                    email: user.email,
                                    id: user.id
                                }
                            });

                            this.filteredAzureUsers = cloneDeep(this.unfilteredAzureUsers);
                        }
                    }).catch(error => {
                        let errorTitle = 'Failed to Fetch Users';

                        let errorMsg;

                        if(error.response?.data?.data) {
                            errorMsg = "Error: \n\n" + error.response.data.data;
                        } else {
                            errorMsg = '';
                        }

                        this.onRequestError(errorTitle, errorMsg);
                    });
            },
            async getUsersNotificationCCSettings(userEmail) {
                this.loading = true;

                const tokenResponse = await auth.acquireTokenResponse();

                return apiService.GetUsersNotificationCCSettings(userEmail, this.selectedCCType, tokenResponse.accessToken)
                    .then(res => {
                        if (res.data?.data) {
                            this.assignCCSettings(res.data.data);
                            this.loading = false;
                        }
                    }).catch(error => {
                        let errorTitle = 'Failed to Fetch User CC Settings';

                        let errorMsg;

                        if(error.response?.data?.data) {
                            errorMsg = "Error: \n\n" + error.response.data.data;
                        } else {
                            errorMsg = '';
                        }

                        this.onRequestError(errorTitle, errorMsg);
                    })
            },
            onCloseNotification(flag) {
                this[flag] = false;
            },
            onColFilterChange({ filters }) {
                if (!filters || filters.length === 0) {
                    this.filter = null;
                } else {
                    this.filter = {
                        logic: 'and',
                        filters: filters.map(f => ({ field: f.field, operator: f.operator, value: f.value }))
                    };
                }
                this.getData();
            },
            onDeleteSelectedSettings() {
                this.deleteItems = this.selectedCCRows;
                this.confirmMsg = 'Are you sure you would like to delete these settings?';
                this.toggleConfirmDialog();
            },
            onDeleteSetting(setting) {
                this.deleteItems = [setting];
                this.confirmMsg = `Are you sure you would like to delete this setting?`;
                this.toggleConfirmDialog();
            },
            onEditSetting(setting) {
                this.selectedCCSetting = setting;
                this.toggleEditSettingDialog();
            },
            onHeaderSelectionChange() {
                const checked = !this.areAllSettingsSelected;

                this.filteredUsersCCSettings.forEach(setting => {
                    let match = this.usersCCSettings.find(s => s.guid === setting.guid);
                    if (match) match[this.selectedField] = checked;
                });

                this.getData();
            },
            onRequestError(errorTitle, errorMsg) {
                this.loading = false;

                this.errorTitle = errorTitle;

                if (errorMsg) {
                    this.errorMsg = errorMsg;
                } else {
                    this.errorMsg = 'Please try again';
                }

                this.showErrorDialog = true;
            },
            onRowClick(row) {
                // Row click opens edit dialog (mirrors original Kendo rowclick behavior)
                this.selectedCCSetting = row;
                this.toggleEditSettingDialog();
            },
            onSearchChange(evt) {
                this.searchWord = evt.value;
                this.getData();
            },
            onSettingSelectionChange(row) {
                let setting = this.usersCCSettings.find(s => s.guid === row.guid);
                if (setting) {
                    setting[this.selectedField] = !setting[this.selectedField];
                    this.getData();
                }
            },
            onSettingUpdated() {
                this.toggleEditSettingDialog();
                this.getUsersNotificationCCSettings(this.selectedAzureUser.email);
            },
            onSortChange({ field, dir }) {
                this.sort = [{ field, dir }];
                this.getData();
            },
            onUserFilterChange(evt) {
                this.filteredAzureUsers = filterBy(cloneDeep(this.unfilteredAzureUsers), evt.filter);
            },
            onUserSelected() {
                if (this.selectedAzureUser.email) {
                    this.getUsersNotificationCCSettings(this.selectedAzureUser.email);
                }
            },
            showNotification(notificationMsg) {
                this.notificationMsg = notificationMsg;

                this.successNotification = true;

                setTimeout(() => {
                    this.successNotification = false;
                }, 4000);
            },
            toggleConfirmDialog() {
                this.showConfirmDialog = !this.showConfirmDialog;
            },
            toggleEditSettingDialog() {
                this.showEditSettingDialog = !this.showEditSettingDialog;

                if (!this.showEditSettingDialog) {
                    this.selectedCCSetting = null;
                }
            },
            toggleErrorDialog() {
                this.showErrorDialog = !this.showErrorDialog;

                if(!this.showErrorDialog) {
                    this.errorTitle = '';
                }
            }
        },
        components: {
            'k-loader': Loader,
            'k-dropdown': DropDownList,
            'k-input': Input,
            'k-button': Button,
            'confirm-dialog': confirmDialog,
            'k-notification': Notification,
            'k-notification-group': NotificationGroup,
            'k-fade': Fade,
            'k-dialog': Dialog,
            'edit-setting-dialog': editSettingDialog,
            'command-center-table': CommandCenterTable,
        }
    }
</script>

<style>
    @import '/src/assets/css/editNotificationCC.css';
</style>